/**
 * Monochrome Theme - Clean Black/White/Grey Design System
 *
 * Usage:
 * import { colors, spacing, typography, components, createStyles } from '../styles/monochromeTheme';
 *
 * // Use individual tokens
 * <div style={{ background: colors.bgPrimary, color: colors.textPrimary }}>
 *
 * // Or use the createStyles helper for full page styles
 * const styles = createStyles();
 */

// ========== COLOR TOKENS ==========
export const colors = {
  // Backgrounds
  bgPrimary: '#fafafa',
  bgSecondary: '#fff',
  bgTertiary: '#f8f8f8',
  bgDark: '#000',
  bgOverlay: 'rgba(0,0,0,0.05)',

  // Borders
  borderLight: '#e5e5e5',
  borderMedium: '#e0e0e0',
  borderSubtle: '#eee',

  // Text
  textPrimary: '#000',
  textSecondary: '#333',
  textTertiary: '#666',
  textMuted: '#999',
  textInverse: '#fff',
  textInverseMuted: 'rgba(255,255,255,0.7)',

  // Accent colors
  accentPrimary: '#000',
  accentOrange: '#ea580c',
  accentOrangeBg: 'rgba(234,88,12,0.1)',
  accentSuccess: '#16a34a',
  accentSuccessBg: 'rgba(22,163,74,0.1)',
  accentError: '#dc2626',
  accentErrorBg: 'rgba(220,38,38,0.1)',
};

// ========== SPACING TOKENS ==========
export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  xxl: '24px',
  xxxl: '28px',
  xxxxl: '32px',
};

// ========== BORDER RADIUS ==========
export const radius = {
  sm: '6px',
  md: '10px',
  lg: '12px',
  xl: '14px',
  xxl: '16px',
  xxxl: '20px',
};

// ========== TYPOGRAPHY ==========
export const typography = {
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',

  // Font sizes
  fontSize: {
    xs: '11px',
    sm: '13px',
    md: '14px',
    lg: '15px',
    xl: '28px',
    xxl: '32px',
  },

  // Font weights
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  // Line heights
  lineHeight: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 2,
  },

  // Letter spacing
  letterSpacing: {
    tight: '-0.5px',
    normal: '0',
    wide: '1.5px',
  },
};

// ========== SHADOWS ==========
export const shadows = {
  none: 'none',
  sm: '0 1px 2px rgba(0,0,0,0.05)',
  md: '0 4px 6px rgba(0,0,0,0.05)',
  lg: '0 10px 15px rgba(0,0,0,0.1)',
};

// ========== TRANSITIONS ==========
export const transitions = {
  fast: 'all 0.15s ease',
  normal: 'all 0.2s ease',
  slow: 'all 0.3s ease',
};

// ========== COMPONENT STYLES ==========
export const components = {
  // Page wrapper
  page: {
    minHeight: '100vh',
    background: colors.bgPrimary,
    padding: spacing.xxxxl,
  },

  // Container
  container: {
    maxWidth: '1440px',
    margin: '0 auto',
  },

  // Header
  header: {
    marginBottom: spacing.xxxxl,
    paddingBottom: spacing.xxl,
    borderBottom: `1px solid ${colors.borderLight}`,
  },

  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xl,
  },

  headerIcon: {
    width: '56px',
    height: '56px',
    background: colors.bgDark,
    borderRadius: radius.xxl,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: typography.fontSize.xl,
  },

  headerTitle: {
    margin: 0,
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    color: colors.textPrimary,
    letterSpacing: typography.letterSpacing.tight,
  },

  headerSubtitle: {
    margin: 0,
    fontSize: typography.fontSize.md,
    color: colors.textTertiary,
    marginTop: spacing.xs,
  },

  // Card
  card: {
    background: colors.bgSecondary,
    borderRadius: radius.xxxl,
    border: `1px solid ${colors.borderLight}`,
    padding: spacing.xxxl,
  },

  cardCompact: {
    background: colors.bgSecondary,
    borderRadius: radius.xl,
    border: `1px solid ${colors.borderLight}`,
    padding: spacing.xl,
  },

  // Grid layouts
  gridTwoColumn: {
    display: 'grid',
    gridTemplateColumns: '480px 1fr',
    gap: spacing.xxxxl,
  },

  gridThreeColumn: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: spacing.xxl,
  },

  // Section label (uppercase small text)
  sectionLabel: {
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.bold,
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: typography.letterSpacing.wide,
    marginBottom: spacing.lg,
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
  },

  // Field label
  fieldLabel: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    color: colors.textSecondary,
    marginBottom: spacing.sm,
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
  },

  // Input
  input: {
    height: '48px',
    borderRadius: radius.lg,
    border: `1px solid ${colors.borderMedium}`,
    fontSize: typography.fontSize.md,
    width: '100%',
  },

  inputSmall: {
    height: '40px',
    borderRadius: radius.md,
    border: `1px solid ${colors.borderMedium}`,
    fontSize: typography.fontSize.sm,
    width: '100%',
  },

  // Divider
  divider: {
    height: '1px',
    background: colors.borderSubtle,
    margin: `${spacing.xxl} 0`,
  },

  // Options box (grouped content)
  optionsBox: {
    background: colors.bgTertiary,
    borderRadius: radius.xl,
    padding: spacing.xl,
    border: `1px solid ${colors.borderSubtle}`,
  },

  // Stats box (dark highlight)
  statsBox: {
    background: colors.bgDark,
    borderRadius: radius.xxl,
    padding: spacing.xl,
  },

  statsNumber: {
    fontSize: typography.fontSize.xxl,
    fontWeight: typography.fontWeight.bold,
    color: colors.textInverse,
  },

  statsLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textInverseMuted,
    marginTop: spacing.xs,
  },

  // Primary button
  primaryBtn: {
    height: '56px',
    borderRadius: radius.xl,
    background: colors.bgDark,
    border: 'none',
    color: colors.textInverse,
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    cursor: 'pointer',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    transition: transitions.normal,
  },

  secondaryBtn: {
    height: '48px',
    borderRadius: radius.lg,
    background: colors.bgSecondary,
    border: `1px solid ${colors.borderMedium}`,
    color: colors.textSecondary,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    transition: transitions.normal,
  },

  // Preview/empty state
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '300px',
    textAlign: 'center',
  },

  emptyStateIcon: {
    width: '72px',
    height: '72px',
    background: colors.bgTertiary,
    borderRadius: radius.xxxl,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: typography.fontSize.xxl,
    marginBottom: spacing.xl,
  },

  // Preview content area
  previewContent: {
    background: colors.bgTertiary,
    borderRadius: radius.xl,
    padding: spacing.xxl,
    minHeight: '450px',
  },

  previewText: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    margin: 0,
    fontSize: typography.fontSize.sm,
    lineHeight: typography.lineHeight.relaxed,
    fontFamily: typography.fontFamily,
    color: colors.textSecondary,
  },

  // Loading state
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: colors.bgPrimary,
  },

  loadingText: {
    marginTop: spacing.lg,
    color: colors.textTertiary,
    fontWeight: typography.fontWeight.medium,
  },
};

// ========== DYNAMIC STYLE GENERATORS ==========

/**
 * Generate segment button styles (toggle buttons)
 * @param {boolean} active - Whether the button is active
 * @param {'default' | 'small'} size - Button size variant
 */
export const segmentBtn = (active, size = 'default') => ({
  flex: 1,
  height: size === 'small' ? '44px' : '48px',
  borderRadius: size === 'small' ? radius.md : radius.lg,
  border: active ? 'none' : `1px solid ${colors.borderMedium}`,
  background: active ? colors.bgDark : colors.bgSecondary,
  color: active ? colors.textInverse : colors.textSecondary,
  fontWeight: typography.fontWeight.semibold,
  fontSize: size === 'small' ? typography.fontSize.sm : typography.fontSize.md,
  cursor: 'pointer',
  transition: transitions.normal,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

/**
 * Generate tag/badge styles
 * @param {'default' | 'dark' | 'orange' | 'success' | 'error'} variant
 */
export const tag = (variant = 'default') => {
  const variants = {
    default: { background: colors.bgOverlay, color: colors.textTertiary },
    dark: { background: 'rgba(0,0,0,0.08)', color: colors.textSecondary },
    orange: { background: colors.accentOrangeBg, color: colors.accentOrange },
    success: { background: colors.accentSuccessBg, color: colors.accentSuccess },
    error: { background: colors.accentErrorBg, color: colors.accentError },
  };

  return {
    display: 'inline-flex',
    alignItems: 'center',
    padding: `${spacing.xs} ${spacing.md}`,
    borderRadius: radius.sm,
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.semibold,
    ...variants[variant],
  };
};

/**
 * Create full styles object for a page (convenience function)
 * Returns all component styles plus dynamic generators
 */
export const createStyles = () => ({
  ...components,
  segmentBtn,
  segmentBtnSm: (active) => segmentBtn(active, 'small'),
  tag,
});

// ========== ANT DESIGN OVERRIDES ==========
export const antdOverrides = {
  // DatePicker, TimePicker, Select, InputNumber
  picker: {
    height: '48px',
    borderRadius: radius.lg,
    border: `1px solid ${colors.borderMedium}`,
    fontSize: typography.fontSize.md,
    width: '100%',
  },

  // Checkbox
  checkbox: {
    colorPrimary: colors.accentPrimary,
  },

  // Switch
  switch: {
    colorPrimary: colors.accentPrimary,
  },

  // Spin/Loading
  spin: {
    color: colors.accentPrimary,
  },
};

// ========== THEME OBJECT (for ThemeProvider if needed) ==========
export const monochromeTheme = {
  colors,
  spacing,
  radius,
  typography,
  shadows,
  transitions,
  components,
  segmentBtn,
  tag,
  antdOverrides,
};

export default monochromeTheme;
